import React, {FC, CSSProperties} from 'react';
import {getBackgroundImageProps} from 'app/utilities/ImageProps';
import {ContentAreaProperty, NumberProperty} from '@episerver/spa-core/Property';
import {ContentReferenceProperty, StringProperty} from '@episerver/spa-core/Property';
import IEpiserverContext from '@episerver/spa-core/Core/IEpiserverContext';
import MarketingTextBlockData from 'app/Models/Content/MarketingTextBlockData';
import useFadeIn from 'app/Components/Shared/UseFadeIn';
import classNames from 'classnames';
import {getTextWithHighlight} from 'app/utilities/highlightText';
import {isMobile} from 'react-device-detect';
import TagName from '../TagName';

type BaseBlock = {
  blockTitle: ContentAreaProperty;
  blockSubtitle: StringProperty;
  desktopTitle: ContentAreaProperty;
  desktopSubtitle: StringProperty;
  blockBackGroundImage: ContentReferenceProperty;
  blockBackGroundMobileImage: ContentReferenceProperty;
  blockBackgroundColor: StringProperty
  buttonLink: StringProperty;
  buttonLinkTitle: StringProperty;
  titleHeadlineTag: NumberProperty;
  subtitleHeadlineTag: NumberProperty;
};

type BlockWrapperProps = {
  props: {
    data: Partial<BaseBlock>;
    context: IEpiserverContext;
  };
  disableFadeIn?: boolean;
};

const BlockWrapper: FC<BlockWrapperProps> = ({props: {data, context}, disableFadeIn = false, children}) => {
  const {fadeClass, ref} = disableFadeIn ? {fadeClass: '', ref: null} : useFadeIn();
  const desktopBackground = getBackgroundImageProps(data.blockBackGroundImage, context)?.style?.backgroundImage;
  const mobileBackground = getBackgroundImageProps(data.blockBackGroundMobileImage, context)?.style?.backgroundImage;
  const colorBackground = data.blockBackgroundColor?.value;

  const title =
    !isMobile && data.desktopTitle?.expandedValue
      ? (data.desktopTitle.expandedValue[0] as MarketingTextBlockData)
      : data.blockTitle?.expandedValue
      ? (data.blockTitle.expandedValue[0] as MarketingTextBlockData)
      : null;
  const isTitle = !!title?.text?.value;

  const subtitle = data.desktopSubtitle?.value ? data.desktopSubtitle.value : data.blockSubtitle?.value ? data.blockSubtitle.value : null;

  const titleHeadLineTag = data.titleHeadlineTag?.value;
  const subtitleHeadLineTag = data.subtitleHeadlineTag?.value;

  const isSubtitle = !!subtitle;
  const isHead = isTitle || isSubtitle;

  const style = {
    '--block-mobile-background': mobileBackground ?? desktopBackground,
    '--block-desktop-background': desktopBackground,
  } as CSSProperties;

  if (colorBackground) {
    style['--block-color-background'] = colorBackground;
  }

  return (
    <div className={classNames('BlockWrapper', fadeClass)} ref={ref} style={style}>
      {isHead && (
        <div className='BlockWrapper__head'>
          {isTitle && (
            <TagName tagName={titleHeadLineTag} isTitle className='BlockWrapper__title'>
              {getTextWithHighlight({text: title.text.value, highlightText: title.highlightText.value})}
            </TagName>
          )}
          {isSubtitle && (
            <TagName tagName={subtitleHeadLineTag} className='BlockWrapper__subtitle'>
              {subtitle}
            </TagName>
          )}
        </div>
      )}

      {children}

      {data.buttonLink?.value && (
        <a className='BlockWrapper__link' href={data.buttonLink?.value}>
          {data.buttonLinkTitle?.value}
        </a>
      )}
    </div>
  );
};

export default BlockWrapper;
